'use client';

import { type EvidenceAwarenessPopupVariant } from '@/components/constants/flags';
import { cn } from '@/lib/cn';
import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import { type ReactElement } from 'react';
import { SessionlessEvidenceAwarenessPopoverCard } from './sessionless-evidence-awareness-popover-card';
import { useSessionlessEvidenceAwarenessPopover } from './useSessionlessEvidenceAwarenessPopover';

export type SessionlessEvidenceAwarenessPlacement = Extract<
  EvidenceAwarenessPopupVariant,
  'floating' | 'sidebar'
>;

export type SessionlessEvidenceAwarenessPopoverProps = {
  variant?: SessionlessEvidenceAwarenessPlacement;
};

/** Card owns surface chrome; strip PopoverContent defaults (incl. experimental ring/shadow). */
const floatingPopoverContentClassName = cn(
  'z-[60] w-[min(calc(100vw-2rem),15rem)] max-w-60',
  '!rounded-none !border-0 !bg-transparent !p-0 !shadow-none !ring-0',
  'data-[experimental=true]:!gap-0 data-[experimental=true]:!rounded-none',
  'data-[experimental=true]:!border-0 data-[experimental=true]:!shadow-none',
  'data-[experimental=true]:!ring-0'
);

/** Evidence awareness surface (not modal-queue). See `useSessionlessEvidenceAwarenessEligibility` + `modalClosedRules` for cadence and dismiss streak rules. */
export function SessionlessEvidenceAwarenessPopover(
  props: SessionlessEvidenceAwarenessPopoverProps = {}
): ReactElement | null {
  const { variant: placement = 'floating' } = props;
  const {
    showPopover,
    title,
    suggestion,
    ctaButtonLabel,
    category,
    onDismiss,
    onCta,
    handleOpenChange,
  } = useSessionlessEvidenceAwarenessPopover();

  const card = showPopover ? (
    <SessionlessEvidenceAwarenessPopoverCard
      title={title}
      suggestion={suggestion}
      ctaButtonLabel={ctaButtonLabel}
      category={category}
      onDismiss={onDismiss}
      onCta={onCta}
      className={placement === 'floating' ? 'min-w-48 max-w-60' : undefined}
    />
  ) : null;

  if (placement === 'sidebar') {
    return card ? (
      <div className="w-full shrink-0 duration-200 animate-in fade-in-0">{card}</div>
    ) : null;
  }

  return (
    <Popover open={showPopover} onOpenChange={handleOpenChange} modal={false}>
      <PopoverAnchor
        className="pointer-events-none absolute bottom-2 start-2 z-[55] size-px"
        aria-hidden
      />
      <PopoverContent
        side="right"
        align="end"
        onInteractOutside={(event) => event.preventDefault()}
        className={floatingPopoverContentClassName}
      >
        {card}
      </PopoverContent>
    </Popover>
  );
}
